<!--
  DigitizeAtSizeDialog -- send a decoration that has NO placements to the
  digitizer, at a size the user gives (Jake, 2026-10-27):

    "We ask for width and height. Aspect ratio is preserved: editing width
     sets height and the reverse. Each placement LOCATION becomes a preset
     chip (left chest etc.) that fills in the largest size fitting that
     location. A size over the location's max shows a warning but is NOT
     blocked (the digitizer can still stitch it)."

  A library artwork that was never placed has no decoration, so there is no
  size for the ordinary send to digitize at. This owns its trigger button and
  the dialog; the host mounts it where the artwork has no decorations and
  re-reads the page on `sent`.

  Method (Jake, 2026-10-28): "I need to be able to choose between embroidery
  and dtf formats. The dialog currently assumes embroidery." The sizing read's
  `methods` lists what this distributor can send, default (embroidery) first;
  with two or more a radio group offers the choice, with one there is no
  choice to show. The send carries the chosen slug as `method`. The lede
  names the method being sized.

  Data: GET /api/artworks/<id>/digitization_sizing gives the shape the size
  locks to (`aspect_ratio`, null when unknown) and one preset per location
  (the server's largest size of that shape inside the location's maximum).
  The lock is useAspectLockedSize -- one side drives, the other follows --
  NOT clamped: a size over the chosen location's maximum is allowed and only
  warned about. Send is POST /api/artworks/<id>/send_to_digitizer with
  {width_in, height_in, method}; the server owns the shape rule (422
  size_aspect_mismatch) and every other refusal, shown here verbatim.

  Fees (Plan A, Jake 2026-10-29): when the Super Admin art-cleanup switch is
  ON, each method in the sizing read carries `fee_usd` (a two-place string)
  and `requires_acknowledgment`. The dialog then states the fee ("Artistic
  Punch will redraw this as vector art. Fee: $X (billed to your account).")
  and shows a REQUIRED checkbox; the send stays disabled until it is ticked
  and carries `fee_acknowledged_usd`, the exact amount shown. The server
  refuses a send without it (422) and logs it. A change of method clears the
  tick (it was for another fee). If the fee moved while the dialog was open
  the server says so (422 `art_cleanup_fee_changed`): the sizing is re-read,
  the new fee shown and the box unticked. With the switch off none of this
  appears and the send is exactly what it was. Nothing is charged here.

  Shown only when the artwork is sendable (not an uploaded stitch file) and
  the caller holds both the send capability and library write (adding a size
  to the library is a library write).

  Opened for a PLACEMENT (Jake, 2026-10-29): the optional "Send for art
  cleanup" button on a low-resolution DTF placement mounts this dialog with
  `trigger` off, `fixedMethod` "dtf" and `fixedSize` the placement's own size
  and calls the exposed `openDialog()`. The method and the size are then not
  choices: the dialog states the size, the fee and the required box, and sends
  exactly that. If the sizing read does not offer the fixed method (no
  digitizer routes it, or the switch is off) it says so and cannot send; it
  NEVER falls back to another method.
-->
<template>
    <div class="dz">
        <button
            v-if="offered && trigger"
            type="button"
            class="dz__btn dz__btn--primary"
            data-testid="dla-digitize-unplaced-btn"
            @click="openDialog"
        >
            Send for digitization
        </button>

        <div
            v-if="open"
            class="dz-backdrop"
            @click.self="onCancel"
        >
            <div
                class="dz-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="dz-title"
                data-testid="dla-dz-dialog"
            >
                <header class="dz-dialog__header">
                    <h3 id="dz-title" class="dz-dialog__title" data-testid="dla-dz-title">
                        {{ actionLabel }}
                    </h3>
                    <button
                        type="button"
                        class="dz-dialog__close"
                        aria-label="Close"
                        @click="onCancel"
                    >
                        &times;
                    </button>
                </header>

                <div class="dz-dialog__body">
                    <p
                        v-if="fixedSize"
                        class="dz-dialog__lede"
                        data-testid="dla-dz-lede"
                    >
                        Artistic Punch will clean this image up for printing
                        at the size of this placement.
                    </p>
                    <p v-else class="dz-dialog__lede" data-testid="dla-dz-lede">
                        This decoration isn't on a product yet, so choose the
                        {{ sizeNoun }} to digitize it at. Start from a
                        location, or type a size.
                    </p>

                    <p
                        v-if="methodUnavailable"
                        class="dz-error"
                        role="alert"
                        data-testid="dla-dz-unavailable"
                    >
                        Art cleanup is not available for this image right now.
                        For help with art cleanup, email {{ supportEmail }}.
                    </p>

                    <fieldset
                        v-if="!fixedMethod && methods.length > 1"
                        class="dz-methods"
                        data-testid="dla-dz-method"
                    >
                        <legend class="dz-methods__legend">Digitize for</legend>
                        <label
                            v-for="m in methods"
                            :key="m.slug"
                            class="dz-method"
                            :class="{ 'dz-method--on': methodSlug === m.slug }"
                        >
                            <input
                                v-model="methodSlug"
                                type="radio"
                                name="dz-method"
                                :value="m.slug"
                                :data-testid="`dla-dz-method-${m.slug}`"
                                :disabled="busy"
                            >
                            <span>{{ m.display_name }}</span>
                        </label>
                    </fieldset>

                    <div
                        v-if="!fixedSize && locations.length"
                        class="dz-chips"
                        role="group"
                        aria-label="Locations"
                    >
                        <button
                            v-for="loc in locations"
                            :key="loc.slug"
                            type="button"
                            class="dz-chip"
                            :class="{ 'dz-chip--on': selectedSlug === loc.slug }"
                            :aria-pressed="String(selectedSlug === loc.slug)"
                            :data-testid="`dla-dz-location-${loc.slug}`"
                            :disabled="busy"
                            @click="pickLocation(loc)"
                        >
                            <span class="dz-chip__label">{{ loc.label }}</span>
                            <span class="dz-chip__size">
                                {{ formatSize(loc.width_in, loc.height_in) }}
                            </span>
                        </button>
                    </div>

                    <div v-if="!fixedSize" class="dz-size-row">
                        <label class="dz-field">
                            <span class="dz-axis">W</span>
                            <input
                                data-testid="dla-dz-width"
                                class="dz-input"
                                type="number"
                                inputmode="decimal"
                                min="0.01"
                                step="0.01"
                                aria-label="Width (inches)"
                                :value="widthText"
                                :disabled="busy"
                                @input="onInput('width', $event.target.value)"
                            >
                        </label>
                        <span
                            v-if="size.locked.value"
                            class="material-icons dz-lock"
                            data-testid="dla-dz-lock"
                            role="img"
                            aria-label="Proportions locked"
                            title="Width and height keep the artwork's proportions"
                        >lock</span>
                        <span
                            v-else
                            class="material-icons dz-lock dz-lock--unknown"
                            data-testid="dla-dz-shape-unknown"
                            role="img"
                            aria-label="Shape unknown: proportions not locked"
                            title="This artwork's shape is not known, so width and height are not locked together"
                        >lock_open</span>
                        <label class="dz-field">
                            <span class="dz-axis">H</span>
                            <input
                                data-testid="dla-dz-height"
                                class="dz-input"
                                type="number"
                                inputmode="decimal"
                                min="0.01"
                                step="0.01"
                                aria-label="Height (inches)"
                                :value="heightText"
                                :disabled="busy"
                                @input="onInput('height', $event.target.value)"
                            >
                        </label>
                        <span class="dz-axis">in</span>
                    </div>

                    <p
                        v-if="hasSize"
                        class="dz-summary"
                        data-testid="dla-dz-size"
                    >{{ formatSize(size.width.value, size.height.value) }}</p>

                    <p
                        v-if="overMax"
                        class="dz-warning"
                        role="status"
                        data-testid="dla-dz-over-max"
                    >
                        Larger than {{ selected.label }}'s maximum of
                        {{ formatSize(selected.max_width_in, selected.max_height_in) }}.
                        The digitizer can still stitch it at this size.
                    </p>

                    <div
                        v-if="needsAck"
                        class="dz-fee"
                        data-testid="dla-dz-fee-block"
                    >
                        <p class="dz-fee__text" data-testid="dla-dz-fee">{{ feeText }}</p>
                        <label class="dz-fee__ack">
                            <input
                                v-model="ackChecked"
                                type="checkbox"
                                data-testid="dla-dz-ack"
                                :disabled="busy"
                            >
                            <span data-testid="dla-dz-ack-label">{{ ackLabel }}</span>
                        </label>
                    </div>

                    <p
                        v-if="error"
                        class="dz-error"
                        role="alert"
                        data-testid="dla-dz-error"
                    >
                        {{ error }}
                    </p>

                    <div class="dz-dialog__footer">
                        <button
                            type="button"
                            class="dz__btn"
                            data-testid="dla-dz-cancel"
                            :disabled="busy"
                            @click="onCancel"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            class="dz__btn dz__btn--primary"
                            data-testid="dla-dz-send"
                            :disabled="!canSend"
                            :aria-disabled="String(!canSend)"
                            @click="send"
                        >
                            {{ busy ? 'Sending...' : actionLabel }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import axios from '@/axios'
import { useAspectLockedSize } from '@/composables/useAspectLockedSize'
import { useSupportEmail } from '@/composables/useSupportEmail'

const SEND_CAP = 'distributor.digitization.send'
const LIBRARY_WRITE_CAP = 'distributor.decoration_library.write'

const props = defineProps({
    artworkId: { type: Number, required: true },
    /** The caller's capability keys (the host's resolved list). */
    capabilities: { type: Array, default: () => [] },
    /** False when a host owns the opening (the cleanup button on a placement). */
    trigger: { type: Boolean, default: true },
    /** A method slug that is NOT a choice (e.g. "dtf"); '' = the user picks. */
    fixedMethod: { type: String, default: '' },
    /** A size that is NOT a choice, `{ width, height }` in inches; null = the user picks. */
    fixedSize: { type: Object, default: null },
})

const emit = defineEmits(['sent'])
const { supportEmail } = useSupportEmail()

const sizing = ref(null)
const open = ref(false)
const busy = ref(false)
const error = ref('')
const selectedSlug = ref(null)
const widthText = ref('')
const heightText = ref('')
const DEFAULT_METHOD = 'embroidery'
const methodSlug = ref(DEFAULT_METHOD)

// What each method's size is called. An unknown slug falls back to its name.
const SIZE_NOUNS = {
    embroidery: 'embroidery size',
    dtf: 'DTF print size',
}

// The methods on offer, default first. A response without the list (older
// server) is embroidery alone.
const methods = computed(() => {
    const listed = sizing.value?.methods
    if (Array.isArray(listed)) return listed
    const single = sizing.value?.method
    return single ? [single] : []
})
const sizeNoun = computed(
    () => SIZE_NOUNS[methodSlug.value] || `${methodSlug.value} size`,
)

// The method being sent, as the sizing read describes it (null until read).
const currentMethod = computed(
    () => methods.value.find((m) => m.slug === methodSlug.value) || null,
)
// The fee to disclose, and whether the send must carry its approval. Both
// come from the server: with the art-cleanup switch off there is neither.
const feeUsd = computed(() => currentMethod.value?.fee_usd ?? null)
const needsAck = computed(
    () => Boolean(currentMethod.value?.requires_acknowledgment) && feeUsd.value != null,
)
const ackChecked = ref(false)
const isDtf = computed(() => methodSlug.value === 'dtf')
// Opened for a placement's DTF image the action is a cleanup, not a digitizing.
const actionLabel = computed(() =>
    props.fixedMethod === 'dtf' ? 'Send for art cleanup' : 'Send for digitization',
)
const feeText = computed(() => {
    const what = isDtf.value
        ? 'redraw this as vector art'
        : 'digitize this as an embroidery stitch file'
    return `Artistic Punch will ${what}. Fee: $${feeUsd.value} (billed to your account).`
})
const ackLabel = computed(() =>
    isDtf.value
        ? 'I approve sending this file for cleanup and the fee.'
        : 'I approve sending this file for digitization and the fee.',
)

const locations = computed(() => sizing.value?.locations ?? [])
const selected = computed(
    () => locations.value.find((loc) => loc.slug === selectedSlug.value) || null,
)

// Unclamped: the chosen location only decides what "too big" warns about.
const size = useAspectLockedSize({
    aspect: () => sizing.value?.aspect_ratio ?? null,
    maxWidth: () => selected.value?.max_width_in ?? null,
    maxHeight: () => selected.value?.max_height_in ?? null,
})

const offered = computed(
    () =>
        Boolean(sizing.value?.sendable) &&
        props.capabilities.includes(SEND_CAP) &&
        props.capabilities.includes(LIBRARY_WRITE_CAP),
)

const hasSize = computed(
    () => isPositive(size.width.value) && isPositive(size.height.value),
)
const overMax = computed(
    () => Boolean(selected.value) && hasSize.value && !size.withinEnvelope.value,
)
// A fixed method the sizing read does not offer: say so, never send another.
const methodUnavailable = computed(
    () =>
        Boolean(props.fixedMethod) &&
        Boolean(sizing.value) &&
        !methods.value.some((m) => m.slug === props.fixedMethod),
)
const canSend = computed(
    () =>
        !methodUnavailable.value &&
        hasSize.value &&
        size.shapeOk.value &&
        !busy.value &&
        (!needsAck.value || ackChecked.value),
)

function isPositive(n) {
    return typeof n === 'number' && Number.isFinite(n) && n > 0
}

function formatSize(w, h) {
    return `${Number(w).toFixed(2)} x ${Number(h).toFixed(2)} in`
}

function text(n) {
    return isPositive(n) ? String(n) : ''
}

async function load() {
    sizing.value = null
    try {
        const resp = await axios.get(
            `/api/artworks/${props.artworkId}/digitization_sizing`,
        )
        sizing.value = resp?.data ?? null
    } catch (_err) {
        // No sizing, no offer: the page still shows its empty state.
        sizing.value = null
    }
}

watch(() => props.artworkId, load, { immediate: true })

// The approval was for the fee shown, so it ends with the method that showed it.
watch(methodSlug, () => {
    ackChecked.value = false
})

/** Re-read the sizing in place (the fee moved), keeping the dialog's state. */
async function refreshSizing() {
    try {
        const resp = await axios.get(
            `/api/artworks/${props.artworkId}/digitization_sizing`,
        )
        if (resp?.data) sizing.value = resp.data
    } catch (_err) {
        // The server's sentence is already shown; keep what we have.
    }
}

function reset() {
    methodSlug.value =
        props.fixedMethod || (methods.value[0]?.slug ?? DEFAULT_METHOD)
    selectedSlug.value = null
    const fixed = props.fixedSize
    if (fixed && isPositive(fixed.width) && isPositive(fixed.height)) {
        size.setSize(fixed.width, fixed.height)
        widthText.value = text(size.width.value)
        heightText.value = text(size.height.value)
    } else {
        size.setSize(null, null)
        widthText.value = ''
        heightText.value = ''
    }
    error.value = ''
    ackChecked.value = false
}

function openDialog() {
    reset()
    open.value = true
}

defineExpose({ openDialog })

function pickLocation(loc) {
    selectedSlug.value = loc.slug
    size.setSize(loc.width_in, loc.height_in)
    widthText.value = text(size.width.value)
    heightText.value = text(size.height.value)
    error.value = ''
}

function onInput(side, raw) {
    const n = raw === '' || raw == null ? null : Number(raw)
    if (side === 'width') widthText.value = raw
    else heightText.value = raw
    if (!isPositive(n)) return
    // The typed side keeps what was typed; only the other side follows.
    if (side === 'width') {
        size.setWidth(n, { clamp: false })
        heightText.value = text(size.height.value)
    } else {
        size.setHeight(n, { clamp: false })
        widthText.value = text(size.width.value)
    }
    error.value = ''
}

async function send() {
    if (!canSend.value) return
    busy.value = true
    error.value = ''
    try {
        const resp = await axios.post(
            `/api/artworks/${props.artworkId}/send_to_digitizer`,
            {
                width_in: size.width.value,
                height_in: size.height.value,
                method: methodSlug.value,
                // The exact amount shown and approved; absent when no fee applies.
                ...(needsAck.value ? { fee_acknowledged_usd: feeUsd.value } : {}),
            },
        )
        open.value = false
        emit('sent', resp?.data ?? null)
    } catch (err) {
        if (err?.response?.data?.code === 'art_cleanup_fee_changed') {
            // The approval no longer matches the fee: show the new one.
            ackChecked.value = false
            await refreshSizing()
        }
        const msg = err?.response?.data?.error
        error.value =
            typeof msg === 'string' && msg.trim()
                ? msg
                : 'Could not send this decoration for digitization. Please try again.'
    } finally {
        busy.value = false
    }
}

function onCancel() {
    if (busy.value) return
    open.value = false
}
</script>

<style scoped>
.dz__btn {
    padding: 6px 14px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    background: #fff;
    cursor: pointer;
    font-size: 14px;
}
.dz__btn--primary {
    background: #1976d2;
    color: #fff;
    border-color: #1976d2;
}
.dz__btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
.dz-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    /* Above a Vuetify v-dialog (2400): the cleanup dialog opens from inside one. */
    z-index: 3000;
}
.dz-dialog {
    background: var(--tf-bg, #fff);
    border-radius: 8px;
    width: min(520px, 92vw);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
}
.dz-dialog__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.dz-dialog__title {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
}
.dz-dialog__close {
    background: none;
    border: none;
    font-size: 22px;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.6);
}
.dz-dialog__body {
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.dz-dialog__lede {
    margin: 0;
    font-size: 14px;
}
.dz-methods {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    padding: 0;
    border: none;
}
.dz-methods__legend {
    float: left;
    margin-right: 8px;
    padding: 0;
    font-size: 13px;
    color: var(--tf-fg-4, #6b7280);
}
.dz-method {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 14px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    background: #fff;
    cursor: pointer;
    font-size: 13px;
}
.dz-method--on {
    border-color: #1976d2;
    background: rgba(25, 118, 210, 0.08);
}
.dz-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.dz-chip {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 4px 10px;
    border-radius: 14px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    background: #fff;
    cursor: pointer;
    font-size: 13px;
}
.dz-chip--on {
    border-color: #1976d2;
    background: rgba(25, 118, 210, 0.08);
}
.dz-chip__size {
    font-size: 11px;
    color: var(--tf-fg-4, #6b7280);
}
.dz-size-row {
    display: flex;
    align-items: center;
    gap: 6px;
}
.dz-field {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.dz-axis {
    font-size: 12px;
    color: var(--tf-fg-4, #6b7280);
}
.dz-input {
    width: 80px;
    font-size: 13px;
    padding: 4px 6px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 4px;
}
.dz-lock {
    font-size: 16px;
    color: var(--tf-fg-3, #4b5563);
}
.dz-lock--unknown {
    opacity: 0.5;
}
.dz-summary {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
}
.dz-warning {
    margin: 0;
    font-size: 13px;
    color: #8a5a00;
    background: #fff4e0;
    border-radius: 4px;
    padding: 6px 8px;
}
.dz-fee {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 4px;
    background: #f3f6fb;
}
.dz-fee__text {
    margin: 0;
    font-size: 13px;
}
.dz-fee__ack {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 13px;
    cursor: pointer;
}
.dz-error {
    color: #d32f2f;
    font-size: 13px;
    margin: 0;
}
.dz-dialog__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}
</style>
